import React, { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import LoadingButton from "@mui/lab/LoadingButton";
import { ReactComponent as ChevronDown } from "../../assets/Icons/chevron-down.svg";
import { useCommon } from "../../contexts/common";
import { useCredentials } from "../../contexts/Credentials";
import { LOADING, SUCCESS } from "../../constants/common";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, useWatch } from "react-hook-form";
import { profileCredentialsSchema } from "../../validations/profileValidations";
import { trackClickEvent } from "../../mixpanel";

const CredentialsDrawer = ({
  onClose,
  open,
  activeCredential,
  credentialType,
}) => {
  const { statesList, fetchStatesList, licensesList, fetchLicensesList } =
    useCommon();
  const {
    generateCredential,
    generateMembership,
    createStatus,
    fetchCredentials,
    editCredential,
    editMembership,
    updateStatus,
    updateCredentialstate,
    openConfirmationModal,
    deleteStatus,
  } = useCredentials();
  const initialState = {
    name: "",
    licenseType: null,
    title: "",
    state: null,
    number: "",
    year: "",
    supervisorName: "",
    supervisorLicenseType: null,
    supervisorState: null,
  };

  const isEdit = Boolean(activeCredential);
  const [state, setState] = useState(initialState);

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    formState: { errors, isValid },
    setValue,
  } = useForm({
    resolver: zodResolver(profileCredentialsSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      licenseType: null,
      title: "",
      state: null,
      number: "",
      year: "",
      supervisorName: "",
      supervisorLicenseType: null,
      supervisorState: null,
    },
  });

  const formValues = useWatch({ control });

  useEffect(() => {
    setState({ ...formValues });
  }, [formValues]);

  useEffect(() => {
    fetchStatesList();
    fetchLicensesList();
  }, []);

  useEffect(() => {
    if (createStatus === SUCCESS) {
      updateCredentialstate("createStatus", "");
      fetchCredentials();
      handleClose();
    }
    if (updateStatus === SUCCESS) {
      updateCredentialstate("updateStatus", "");
      fetchCredentials();
      handleClose();
    }
  }, [createStatus, updateStatus]);

  useEffect(() => {
    if (deleteStatus === SUCCESS) {
      handleClose();
    }
  }, [deleteStatus]);

  useEffect(() => {
    reset();
    if (isEdit && open) {
      setValue("name", activeCredential?.type || "");
      setValue("licenseType", activeCredential?.type_id || null);
      setValue("year", activeCredential?.data?.year || "");
      setValue("state", activeCredential?.state_id || null);
      setValue(
        "title",
        activeCredential?.data?.board || activeCredential?.data?.title || "",
      );
      setValue("number", activeCredential?.data?.number || "");
      setValue("supervisorName", activeCredential?.data?.supervisor_name || "");
      setValue("supervisorLicenseType", activeCredential?.type_id || null);
      setValue("supervisorState", activeCredential?.state_id || null);
    } else if (
      !isEdit &&
      open &&
      credentialType &&
      credentialType === "membership"
    ) {
      setValue("name", credentialType);
    }
  }, [activeCredential, open, isEdit, credentialType]);

  const handleInput = (ev) => {
    const { name, value } = ev.target;
    setValue(`${name}`, value);
    if (name !== "name") trigger(name);
  };

  const handleClose = () => {
    onClose();
    setState(initialState);
  };

  const onSubmit = () => {
    if (isValid) {
      let cleanedData = { ...state };

      if (state.name === "license") {
        // Remove pre-licensure fields for license credentials
        delete cleanedData.supervisorName;
        delete cleanedData.supervisorLicenseType;
        delete cleanedData.supervisorState;
      } else if (state.name === "pre-license") {
        // Remove license fields for pre-licensure credentials
        delete cleanedData.licenseType;
        delete cleanedData.title;
        delete cleanedData.number;
        delete cleanedData.year;
        delete cleanedData.state;
      } else if (state.name === "membership") {
        // Remove license and pre-licensure fields for membership credentials
        delete cleanedData.licenseType;
        delete cleanedData.supervisorName;
        delete cleanedData.supervisorLicenseType;
        delete cleanedData.supervisorState;
        delete cleanedData.state;
      }

      if (isEdit) {
        if (state.name === "membership") {
          editMembership({
            credential_type: state.name,
            credential_data: cleanedData,
            credential_id: activeCredential?.id,
          });
          trackClickEvent("Updated Profile Information", {
            component: `Credential Updated Membership`,
            page: "Edit Profile",
            button: "Save",
          });
        } else {
          editCredential({
            credential_type: state.name,
            state_id:
              state.name === "license"
                ? state.state
                : state.name === "pre-license"
                  ? state.supervisorState
                  : null,
            credential_data: cleanedData,
            credential_id: activeCredential?.id,
          });
          trackClickEvent("Updated Profile Information", {
            component: `Credential Updated ${state.name}`,
            page: "Edit Profile",
            button: "Save",
          });
        }
      } else {
        if (state.name === "membership") {
          generateMembership({
            credential_type: state.name,
            credential_data: cleanedData,
          });
          trackClickEvent("Updated Profile Information", {
            component: `Credential Created Membership`,
            page: "Edit Profile",
            button: "Create",
          });
        } else {
          generateCredential({
            credential_type: state.name,
            state_id:
              state.name === "license"
                ? state.state
                : state.name === "pre-license"
                  ? state.supervisorState
                  : null,
            credential_data: cleanedData,
          });
          trackClickEvent("Updated Profile Information", {
            component: `Credential Created ${state.name}`,
            page: "Edit Profile",
            button: "Create",
          });
        }
        // generateCredential({
        //   credential_type: state.name,
        //   state_id: state.name === 'license' ? state.state : (state.name === 'pre-license' ? state.supervisorState : null),
        //   credential_data: cleanedData
        // });
      }
    }
  };

  return (
    <Drawer open={open} onClose={handleClose} anchor="right">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          pt: 8,
        }}
      >
        <Box pb={3} px={3}>
          <Typography variant="h4" mb={1}>
            Credential
          </Typography>
          <Typography variant="body1" mb={3}>
            Please provide your credential information:
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    variant="outlined"
                    label="Credential Type"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    SelectProps={{
                      IconComponent: () => (
                        <Box
                          sx={{
                            minWidth: 24,
                            width: 24,
                            height: 24,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                            mr: 2,
                          }}
                        >
                          <ChevronDown width={12} height={6} />
                        </Box>
                      ),
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 220,
                            width: 250,
                          },
                        },
                      },
                    }}
                    onChange={handleInput}
                    value={state.name}
                    disabled={isEdit || credentialType === "membership"}
                  >
                    <MenuItem value={""} disabled sx={{ py: 0 }} />
                    <MenuItem value="license" sx={{ py: 1 }}>
                      License
                    </MenuItem>
                    <MenuItem value="pre-license" sx={{ py: 1 }}>
                      Pre-Licensure
                    </MenuItem>
                    {credentialType === "membership" && (
                      <MenuItem value="membership" sx={{ py: 1 }}>
                        Membership
                      </MenuItem>
                    )}
                  </TextField>
                )}
              />
            </Grid>
            {Boolean(state.name) && (
              <>
                {state.name === "license" && (
                  <Grid item xs={12}>
                    <Controller
                      name="licenseType"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          variant="outlined"
                          label="License Type"
                          fullWidth
                          error={!!errors.licenseType}
                          helperText={errors.licenseType?.message}
                          SelectProps={{
                            IconComponent: () => (
                              <Box
                                sx={{
                                  minWidth: 24,
                                  width: 24,
                                  height: 24,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                  mr: 2,
                                }}
                              >
                                <ChevronDown width={12} height={6} />
                              </Box>
                            ),
                            MenuProps: {
                              PaperProps: {
                                style: {
                                  maxHeight: 220,
                                  width: 250,
                                },
                              },
                            },
                          }}
                          onChange={handleInput}
                          value={state.licenseType || ""}
                        >
                          <MenuItem value={0} disabled>
                            Select a license type
                          </MenuItem>
                          {licensesList?.map((licenseType) => (
                            <MenuItem
                              key={licenseType.id}
                              value={licenseType.id}
                            >
                              {licenseType.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                )}
                {state.name === "membership" && (
                  <>
                    <Grid item xs={12}>
                      <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            variant="outlined"
                            label="Membership Organization"
                            fullWidth
                            value={state.title}
                            error={!!errors.title}
                            helperText={errors.title?.message}
                            onChange={handleInput}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        name="number"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            variant="outlined"
                            label="Membership Number"
                            fullWidth
                            value={state.number}
                            error={!!errors.number}
                            helperText={errors.number?.message}
                            onChange={handleInput}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        name="year"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            variant="outlined"
                            label="Membership Year (YYYY)"
                            fullWidth
                            value={state.year}
                            error={!!errors.year}
                            helperText={errors.year?.message}
                            onChange={handleInput}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                {state.name === "pre-license" && (
                  <Grid item xs={12}>
                    <Controller
                      name="supervisorName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          label="Supervisor's Name"
                          fullWidth
                          value={state.supervisorName}
                          error={!!errors.supervisorName}
                          helperText={errors.supervisorName?.message}
                          onChange={handleInput}
                        />
                      )}
                    />
                  </Grid>
                )}
                {state.name === "pre-license" && (
                  <Grid item xs={12}>
                    <Controller
                      name="supervisorLicenseType"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          variant="outlined"
                          label="Supervisor's License Type"
                          fullWidth
                          error={!!errors.supervisorLicenseType}
                          helperText={errors.supervisorLicenseType?.message}
                          SelectProps={{
                            IconComponent: () => (
                              <Box
                                sx={{
                                  minWidth: 24,
                                  width: 24,
                                  height: 24,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                  mr: 2,
                                }}
                              >
                                <ChevronDown width={12} height={6} />
                              </Box>
                            ),
                            MenuProps: {
                              PaperProps: {
                                style: {
                                  maxHeight: 220,
                                  width: 250,
                                },
                              },
                            },
                          }}
                          onChange={handleInput}
                          value={state.supervisorLicenseType || ""}
                        >
                          <MenuItem value={0} disabled>
                            Select supervisor's license type
                          </MenuItem>
                          {licensesList?.map((licenseType) => (
                            <MenuItem
                              key={licenseType.id}
                              value={licenseType.id}
                            >
                              {licenseType.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                )}
                {state.name === "pre-license" && (
                  <Grid item xs={12}>
                    <Controller
                      name="supervisorState"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          variant="outlined"
                          label="Supervisor's License State"
                          fullWidth
                          error={!!errors.supervisorState}
                          helperText={errors.supervisorState?.message}
                          SelectProps={{
                            IconComponent: () => (
                              <Box
                                sx={{
                                  minWidth: 24,
                                  width: 24,
                                  height: 24,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                  mr: 2,
                                }}
                              >
                                <ChevronDown width={12} height={6} />
                              </Box>
                            ),
                            MenuProps: {
                              PaperProps: {
                                style: {
                                  maxHeight: 220,
                                  width: 250,
                                },
                              },
                            },
                          }}
                          onChange={handleInput}
                          value={state.supervisorState || 0}
                        >
                          <MenuItem value={0} disabled>
                            Select a state
                          </MenuItem>
                          {statesList.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                )}
                {state.name === "license" && (
                  <Grid item xs={12}>
                    <Controller
                      name="title"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          label="Licensing Board"
                          fullWidth
                          value={state.title}
                          error={!!errors.title}
                          helperText={errors.title?.message}
                          onChange={handleInput}
                        />
                      )}
                    />
                  </Grid>
                )}
                {state.name === "license" && (
                  <>
                    <Grid item xs={12}>
                      <Controller
                        name="state"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            variant="outlined"
                            label="License State"
                            fullWidth
                            error={!!errors.state}
                            helperText={errors.state?.message}
                            SelectProps={{
                              IconComponent: () => (
                                <Box
                                  sx={{
                                    minWidth: 24,
                                    width: 24,
                                    height: 24,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "text.secondary",
                                    mr: 2,
                                  }}
                                >
                                  <ChevronDown width={12} height={6} />
                                </Box>
                              ),
                              MenuProps: {
                                PaperProps: {
                                  style: {
                                    maxHeight: 220,
                                    width: 250,
                                  },
                                },
                              },
                            }}
                            onChange={handleInput}
                            value={state.state}
                          >
                            <MenuItem value={0} disabled>
                              Select a state
                            </MenuItem>
                            {statesList.map((item) => (
                              <MenuItem key={item.id} value={item.id}>
                                {item.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        name="number"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            variant="outlined"
                            label="License Number"
                            fullWidth
                            value={state.number}
                            error={!!errors.number}
                            helperText={errors.number?.message}
                            onChange={handleInput}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        name="year"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            variant="outlined"
                            label="License Year (YYYY)"
                            fullWidth
                            value={state.year}
                            error={!!errors.year}
                            helperText={errors.year?.message}
                            onChange={handleInput}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Please note that the credential information provided will be
                visible to other users on your profile page.
              </Typography>
            </Grid>
            {isEdit && (
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Button
                  onClick={openConfirmationModal}
                  size="small"
                  color="error"
                  sx={{
                    minWidth: 120,
                    backgroundColor: "error.light",
                    color: "error.main",
                  }}
                >
                  Delete
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
        <Box
          sx={{
            boxShadow: "0px 0px 32px rgba(0, 0, 0, 0.08)",
            px: 1,
            pt: 3,
            pb: 5.625,
            display: "flex",
            alignItems: "center",
            gap: 1,
            position: "sticky",
            width: "100%",
            backgroundColor: "background.paper",
            bottom: 0,
            zIndex: 9,
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            sx={{
              color: "primary.main",
            }}
            size="large"
            fullWidth
            onClick={handleClose}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSubmit(onSubmit)}
            loading={createStatus === LOADING}
          >
            {isEdit ? "Save" : "Create"}
          </LoadingButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CredentialsDrawer;
